import { useState, useEffect } from 'react';
import { 
  Modal, Button, Form, Row, Col, 
  Alert, Card, Badge, Spinner 
} from 'react-bootstrap';
import { 
  FaFilePdf, FaFileArchive, FaDownload, 
  FaCalendarAlt, FaInfoCircle, FaCheckCircle,
  FaUsers, FaChartLine, FaHistory, FaBook,
  FaClipboardCheck, FaBox, FaFileInvoice,
  FaRegFileAlt, FaDatabase, FaChartBar,
  FaLock, FaTachometerAlt
} from 'react-icons/fa';
import { toast } from 'sonner';
import '../styles/ReportGenerationModal.css';
import { useReportGeneration } from '../../service/Hooks/Titres/useReportGeneration';

const ReportGenerationModal = ({ 
  show, 
  onHide, 
  security, 
  mode = 'private' 
}) => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [includeZeroBalance, setIncludeZeroBalance] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [periode, setPeriode] = useState('');

  const { 
    loading, 
    error, 
    currentReport, 
    generateReport,
    getReportName,
    getReportFormat,
    getReportDescription,
    getReportRequirements
  } = useReportGeneration();

  // Réinitialiser à l'ouverture
  useEffect(() => {
    if (show) {
      setSelectedReport(null);
      setDateRange({ startDate: '', endDate: '' });
      setIncludeZeroBalance(false);
      setPeriode('');
    }
  }, [show]);

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async () => {
    if (!selectedReport) {
      toast.error('Veuillez sélectionner un type de document');
      return;
    }

    const strictlyNeedsDates = ['account-history'];
    const optionalDates = ['emission-journal'];

    if (strictlyNeedsDates.includes(selectedReport)) {
      if (!dateRange.startDate || !dateRange.endDate) {
        toast.error('Ce rapport nécessite une période (date de début et fin)');
        return;
      }
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      if (start > end) {
        toast.error('La date de début doit être antérieure à la date de fin');
        return;
      }
      if (start > new Date()) {
        toast.error('La date de début ne peut pas être dans le futur');
        return;
      }
    }

    setIsGenerating(true);

    try {
      const options = {
        includeZeroBalance,
        periode: periode ? parseInt(periode) : null,
        ...(selectedReport !== 'operation-comptabilite' && {
          startDate: dateRange.startDate || null,
          endDate: dateRange.endDate || null
        })
      };

      await generateReport(selectedReport, security?.securityId, options);

      setTimeout(() => { onHide(); }, 1500);
    } catch (err) {
      console.error('Erreur génération:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  // Définir les rapports disponibles selon le mode
  const getAvailableReports = () => {
    const allReports = [
      // ── Standards ────────────────────────────────────────────────────────────
      {
        id: 'shareholders-list',
        title: 'Liste des Actionnaires',
        description: 'Liste complète des actionnaires avec leurs participations',
        icon: FaUsers,
        format: 'PDF',
        category: 'standard'
      },
      {
        id: 'accounting-plan',
        title: 'Plan Comptable',
        description: 'Plan comptable détaillé du titre',
        icon: FaChartLine,
        format: 'PDF',
        category: 'standard'
      },
      {
        id: 'account-history',
        title: 'Historique des Comptes',
        description: 'Historique des mouvements par client',
        icon: FaHistory,
        format: 'ZIP',
        category: 'detailed'
      },
      {
        id: 'emission-journal',
        title: 'Journal Auxiliaire',
        description: "Journal des opérations d'émission (JOURNAL AUXILIAIRE VALEUR À LA CONSTITUTION)",
        icon: FaBook,
        format: 'PDF',
        category: 'detailed'
      },

      // ── Marché privé ──────────────────────────────────────────────────────────
      {
        id: 'attestation-propriete',
        title: 'Attestation de Propriété',
        description: 'Attestations de propriété par client',
        icon: FaClipboardCheck,
        format: 'ZIP',
        category: 'marche-prive'
      },
      {
        id: 'compte-conservation',
        title: 'Compte de Conservation',
        description: 'Comptes de conservation des valeurs',
        icon: FaBox,
        format: 'ZIP',
        category: 'marche-prive'
      },
      {
        id: 'registre-conservation',
        title: 'Registre de Conservation',
        description: 'Registre de conservation des titres',
        icon: FaFileInvoice,
        format: 'ZIP',
        category: 'marche-prive'
      },
      {
        id: 'operation-comptabilite',
        title: 'Opération Comptabilité à partie double (AUSCAGIE)',
        description: 'Journal des opérations comptables',
        icon: FaRegFileAlt,
        format: 'PDF',
        category: 'marche-prive'
      },
      {
        id: 'compte-global',
        title: "Compte Global d'Émission",
        description: "État du compte global d'émission",
        icon: FaDatabase,
        format: 'PDF',
        category: 'marche-prive'
      },

      // ── Nouveaux rapports ─────────────────────────────────────────────────────
      {
        id: 'historique-valeur-nominal',
        title: 'Historique Valeur Nominale',
        description: "Évolution historique de la valeur nominale du titre",
        icon: FaChartBar,
        format: 'PDF',
        category: 'marche-prive'
      },
      {
        id: 'restriction-titres',
        title: 'Restriction des Titres',
        description: 'Documents de restriction par représentant légal',
        icon: FaLock,
        format: 'ZIP',
        category: 'marche-prive'
      },
      {
        id: 'tableau-bord-action',
        title: 'Tableau de Bord Action',
        description: 'Synthèse des actions par représentant légal',
        icon: FaTachometerAlt,
        format: 'ZIP',
        category: 'marche-prive'
      },
    ];

    if (mode === 'private') {
      return allReports;
    } else if (mode === 'public') {
      return allReports.filter(r => r.category === 'standard');
    }

    return allReports;
  };

  const availableReports = getAvailableReports();

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      centered
      backdrop="static"
      className="report-generation-modal"
    >
      <Modal.Header closeButton className="bg-white text-white">
        <Modal.Title className="d-flex align-items-center">
          <FaDownload className="me-3" />
          <div className='text text-dark'>
            <h4 className="mb-0 fw-bold">Génération de Documents</h4>
            <small className="opacity-90">
              {security?.securityName || 'Titre sélectionné'}
            </small>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {/* Guide utilisateur */}
        <Alert variant="info" className="mb-4">
          <FaInfoCircle className="me-2" />
          <div>
            <strong>Guide de génération :</strong> Sélectionnez le type de document à générer.
            Certains documents nécessitent une période spécifique.
            <div className="mt-2">
              <Badge bg="light" text="dark" className="me-2">
                <FaFilePdf className="me-1" /> PDF
              </Badge>
              <Badge bg="light" text="dark">
                <FaFileArchive className="me-1" /> ZIP
              </Badge>
            </div>
          </div>
        </Alert>

        {/* Sélection du type de document */}
        <div className="mb-4">
          <h5 className="mb-3 fw-semibold">Sélectionnez un document</h5>
          <Row className="g-3">
            {availableReports.map((report) => {
              const Icon = report.icon;
              const isSelected = selectedReport === report.id;

              return (
                <Col md={4} key={report.id}>
                  <Card 
                    className={`report-type-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => setSelectedReport(report.id)}
                  >
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-center">
                        <div className={`report-icon bg-${isSelected ? 'primary' : 'light'}`}>
                          <Icon className={isSelected ? 'text-white' : 'text-primary'} />
                        </div>
                        <div className="ms-3 flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <h6 className="mb-1 fw-semibold">{report.title}</h6>
                            <Badge 
                              bg={
                                report.format === 'ZIP'
                                  ? 'warning bg-opacity-10 border-warning border border-opacity-25 text-warning'
                                  : 'info bg-opacity-10 border-info border border-opacity-25 text-info'
                              }
                              className="text-uppercase"
                            >
                              {report.format}
                            </Badge>
                          </div>
                          <small className="text-muted d-block">
                            {report.description}
                          </small>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="selected-indicator mt-2">
                          <FaCheckCircle className="text-success me-1" size={14} />
                          <small className="text-success fw-medium">Sélectionné</small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>

        {/* Options spécifiques selon le rapport sélectionné */}
        {selectedReport === 'operation-comptabilite' && (
          <Card className="mb-3 border">
            <Card.Body className="p-3">
              <div className="d-flex align-items-center mb-3">
                <FaCalendarAlt className="me-2 text-primary" />
                <h6 className="mb-0 fw-semibold">Période comptable</h6>
              </div>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Nombre de mois (optionnel, défaut : 3)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="12"
                      placeholder="3"
                      value={periode}
                      onChange={(e) => setPeriode(e.target.value)}
                    />
                    <Form.Text className="text-muted">
                      Laisser vide pour utiliser la période par défaut (3 mois)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {selectedReport && (
          <div className="options-section mb-4">
            <h6 className="mb-3 fw-semibold">Options de génération</h6>

            {/* Période pour les rapports qui en ont besoin */}
            {getReportRequirements(selectedReport) && selectedReport !== 'operation-comptabilite' && (
              <Card className="mb-3 border">
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center mb-3">
                    <FaCalendarAlt className="me-2 text-primary" />
                    <h6 className="mb-0 fw-semibold">Période d'analyse</h6>
                  </div>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Date de début</Form.Label>
                        <Form.Control
                          type="date"
                          value={dateRange.startDate}
                          onChange={(e) => handleDateChange('startDate', e.target.value)}
                          max={new Date().toISOString().split('T')[0]}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Date de fin</Form.Label>
                        <Form.Control
                          type="date"
                          value={dateRange.endDate}
                          onChange={(e) => handleDateChange('endDate', e.target.value)}
                          max={new Date().toISOString().split('T')[0]}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <small className="text-muted mt-2 d-block">
                    {getReportRequirements(selectedReport)}
                  </small>
                </Card.Body>
              </Card>
            )}

            {/* Option solde zéro (actionnaires uniquement) */}
            {selectedReport === 'shareholders-list' && (
              <Form.Check 
                type="switch"
                id="include-zero-balance"
                label="Inclure les comptes avec solde zéro"
                checked={includeZeroBalance}
                onChange={(e) => setIncludeZeroBalance(e.target.checked)}
                className="mb-3"
              />
            )}

            {/* Résumé de la sélection */}
            <Alert variant="light" className="border">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>{getReportName(selectedReport)}</strong>
                  <div className="text-muted">
                    Format : {getReportFormat(selectedReport)} •{' '}
                    {getReportFormat(selectedReport) === 'ZIP'
                      ? '(Archive multiple)'
                      : '(Document unique)'}
                  </div>
                </div>
                <Badge
                  bg="bg-primary bg-opacity-10 border-primary border border-opacity-25 text-primary"
                  className="px-3 py-2"
                >
                  {getReportFormat(selectedReport)}
                </Badge>
              </div>
            </Alert>
          </div>
        )}

        {/* Messages d'erreur */}
        {error && (
          <Alert variant="danger" className="mb-3">
            <FaInfoCircle className="me-2" />
            {error}
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer className="border-top px-4 py-3">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            <small className="text-muted">
              {selectedReport
                ? `${getReportName(selectedReport)} (${getReportFormat(selectedReport)})`
                : 'Sélectionnez un document'
              }
            </small>
          </div>

          <div className="d-flex gap-2">
            <Button 
              variant="light" 
              onClick={onHide}
              disabled={loading || isGenerating}
            >
              Annuler
            </Button>

            <Button 
              variant="primary"
              onClick={handleGenerate}
              disabled={!selectedReport || loading || isGenerating}
              className="d-flex align-items-center px-4"
            >
              {loading || isGenerating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Génération...
                </>
              ) : (
                <>
                  <FaDownload className="me-2" />
                  Générer et Télécharger
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ReportGenerationModal;